function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

/** FastAPI request-validation entries: `{ loc: ['body', 'amount'], msg: 'Field required' }`. */
function validationMessage(detail: unknown[]): string | undefined {
  const messages = detail.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const msg = nonEmptyString((entry as { msg?: unknown }).msg)?.replace(/^Value error, /, '')
    if (!msg) return []
    const loc = (entry as { loc?: unknown }).loc
    const field = Array.isArray(loc)
      ? loc.filter((part) => part !== 'body' && part !== 'query' && part !== 'path').join('.')
      : ''
    return [field ? `${field.replaceAll('_', ' ')}: ${msg}` : msg]
  })
  return messages.length > 0 ? messages.join('; ') : undefined
}

function detailMessage(detail: unknown): string | undefined {
  const text = nonEmptyString(detail)
  if (text) return text
  if (Array.isArray(detail)) return validationMessage(detail)
  if (!detail || typeof detail !== 'object') return undefined
  return (
    nonEmptyString((detail as { error?: unknown }).error) ??
    nonEmptyString((detail as { message?: unknown }).message)
  )
}

/** A readable message from a response body that is JSON (`{"detail": ...}`) or plain text. */
function bodyMessage(text: string): string | undefined {
  const trimmed = text.trim()
  if (!trimmed) return undefined
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object' && 'detail' in parsed) {
        return detailMessage((parsed as { detail: unknown }).detail)
      }
    } catch {
      // Not JSON after all; fall through to the plain-text rules.
    }
    return undefined
  }
  // HTML error pages (proxies, gateways) are never readable as a message.
  if (trimmed.startsWith('<')) return undefined
  return trimmed.length > 300 ? `${trimmed.slice(0, 300)}…` : trimmed
}

/**
 * Extract a readable message from errors thrown by OpenAPI mutations.
 *
 * openapi-fetch throws parsed FastAPI response bodies as plain objects, while
 * network/runtime failures are native Errors. Some existing endpoints wrap
 * their message one level deeper under `detail.error` or `detail.message`,
 * and request validation failures arrive as a `detail` list. An Error whose
 * message is a raw response body (`{"detail":"..."}`) is unwrapped too.
 */
export function getApiErrorMessage(error: unknown, fallback = 'Unknown error'): string {
  if (error instanceof Error) {
    const message = nonEmptyString(error.message)
    if (!message) return fallback
    return message.startsWith('{') ? (bodyMessage(message) ?? fallback) : message
  }

  if (!error || typeof error !== 'object' || !('detail' in error)) {
    return fallback
  }

  return detailMessage((error as { detail: unknown }).detail) ?? fallback
}

export function formatApiErrorMessage(
  prefix: string,
  error: unknown,
  fallback = 'Unknown error',
): string {
  return `${prefix}: ${getApiErrorMessage(error, fallback)}`
}

/** An Error carrying the readable message from a failed fetch Response. */
export async function responseError(response: Response, fallback: string): Promise<Error> {
  const text = await response.text().catch(() => '')
  return new Error(bodyMessage(text) ?? fallback)
}
